import {
  ArrowRight,
  CalendarDays,
  ClipboardList,
  Info,
  Layers3,
  Pencil,
  ShoppingCart,
  UserRoundCheck,
} from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { formatShmpCourse } from '@/lib/shmpCourse'
import { formatCurrency } from '@/lib/utils'
import type {
  ShmpCourseSummary,
  ShmpCourseWeeklyPlanRow,
  ShmpDashboardSummary,
  ShmpPartnerMetrics,
} from '@/types'

function number(value: number): string {
  return Math.round(value || 0).toLocaleString('ru-RU')
}

function percent(value: number | null): string {
  return value === null ? '—' : `${value.toLocaleString('ru-RU')}%`
}

function tone(value: number | null): string {
  if (value === null) return 'text-muted-foreground'
  if (value >= 100) return 'text-success'
  if (value >= 80) return 'text-amber-500'
  return 'text-error'
}

function CardTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-5 flex items-center gap-1.5 text-sm font-semibold">
      {children}
      <Info size={13} className="text-muted-foreground/60" />
    </div>
  )
}

export function ShmpPlanCard({ summary }: { summary: ShmpDashboardSummary }) {
  const completion = summary.plan_completion_pct ?? 0
  const deviation = summary.revenue_fact - summary.revenue_plan
  const gauge = Math.max(0, Math.min(100, completion))

  return (
    <Card className="h-full border border-border/70 shadow-none">
      <CardTitle>План отдела ШМП</CardTitle>
      <div className="grid grid-cols-[130px_1fr] items-center gap-4">
        <div className="relative h-[82px] overflow-hidden">
          <div
            className="absolute left-1/2 top-0 h-[130px] w-[130px] -translate-x-1/2 rounded-full"
            style={{
              background: `conic-gradient(from 270deg, #ffb000 0deg ${gauge * 1.8}deg, #e9edf2 ${gauge * 1.8}deg 180deg, transparent 180deg)`,
            }}
          />
          <div className="absolute left-1/2 top-[10px] h-[110px] w-[110px] -translate-x-1/2 rounded-full bg-card" />
          <div className="absolute inset-x-0 top-10 text-center">
            <div className="text-2xl font-bold">{percent(summary.plan_completion_pct)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">Выполнение плана</div>
          </div>
        </div>
        <dl className="space-y-2.5 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">План</dt>
            <dd className="font-semibold">{formatCurrency(summary.revenue_plan)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Факт</dt>
            <dd className="font-semibold">{formatCurrency(summary.revenue_fact)}</dd>
          </div>
          <div className="border-t border-border pt-2.5">
            <dt className="mb-1 text-muted-foreground">Отклонение</dt>
            <dd className={`font-semibold ${deviation >= 0 ? 'text-success' : 'text-error'}`}>
              {deviation >= 0 ? '+' : ''}{formatCurrency(deviation)}
            </dd>
          </div>
        </dl>
      </div>
    </Card>
  )
}

export type ShmpPlanFactMode = 'app' | 'manual'

export function ShmpPlanFactCard(props: {
  mode: ShmpPlanFactMode
  plan: number
  fact: number
  onModeChange: (mode: ShmpPlanFactMode) => void
  onEditManualPlan?: () => void
}) {
  const completion = props.plan > 0
    ? Math.round(props.fact / props.plan * 1000) / 10
    : null
  const deviation = props.fact - props.plan
  const gauge = Math.max(0, Math.min(100, completion ?? 0))

  return (
    <Card className="h-full border border-border/70 shadow-none">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Layers3 size={14} className="text-muted-foreground/70" />
          План-факт ШМП
        </div>
        <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1 text-xs">
          <button
            type="button"
            onClick={() => props.onModeChange('app')}
            className={`rounded-md px-3 py-1.5 ${
              props.mode === 'app' ? 'bg-card shadow-sm' : 'text-muted-foreground'
            }`}
          >
            По приложению
          </button>
          <button
            type="button"
            onClick={() => props.onModeChange('manual')}
            className={`rounded-md px-3 py-1.5 ${
              props.mode === 'manual' ? 'bg-card shadow-sm' : 'text-muted-foreground'
            }`}
          >
            По листку
          </button>
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
        <span>
          {props.mode === 'app'
            ? 'Факт из приложения и план студий'
            : 'Факт по листку и ручной план ШМП'}
        </span>
        {props.mode === 'manual' && props.onEditManualPlan && (
          <Button
            size="sm"
            variant="outline"
            onClick={props.onEditManualPlan}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border-warning/50 px-2 py-1 text-[10px] leading-none text-warning hover:bg-warning/10 hover:text-warning"
          >
            <Pencil size={12} />
            План ШМП
          </Button>
        )}
      </div>

      <div className="grid grid-cols-[130px_1fr] items-center gap-4">
        <div className="relative h-[90px] overflow-hidden">
          <div
            className="absolute left-1/2 top-0 h-[130px] w-[130px] -translate-x-1/2 rounded-full"
            style={{
              background: `conic-gradient(from 270deg, #ffb000 0deg ${gauge * 1.8}deg, #e9edf2 ${gauge * 1.8}deg 180deg, transparent 180deg)`,
            }}
          />
          <div className="absolute left-1/2 top-[10px] h-[110px] w-[110px] -translate-x-1/2 rounded-full bg-card" />
          <div className="absolute inset-x-0 top-10 text-center">
            <div className="text-2xl font-bold">{percent(completion)}</div>
            <div className="mt-1 text-[10px] text-muted-foreground">Выполнение плана</div>
          </div>
        </div>
        <dl className="space-y-2.5 text-xs">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">План</dt>
            <dd className="font-semibold">{formatCurrency(props.plan)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Факт</dt>
            <dd className="font-semibold">{formatCurrency(props.fact)}</dd>
          </div>
          <div className="border-t border-border pt-2.5">
            <dt className="mb-1 text-muted-foreground">Отклонение</dt>
            <dd className={`font-semibold ${deviation >= 0 ? 'text-success' : 'text-error'}`}>
              {deviation >= 0 ? '+' : ''}{formatCurrency(deviation)}
            </dd>
          </div>
        </dl>
      </div>
    </Card>
  )
}

export function ShmpForecastCard(props: {
  summary: ShmpDashboardSummary
  forecast: number | null
}) {
  const forecastPct = props.forecast !== null && props.summary.revenue_plan > 0
    ? props.forecast / props.summary.revenue_plan * 100
    : null
  const remaining = props.forecast === null
    ? null
    : props.forecast - props.summary.revenue_plan

  return (
    <Card className="h-full border border-border/70 shadow-none">
      <CardTitle>Прогноз выполнения плана</CardTitle>
      <div className="text-xs text-muted-foreground">Прогноз на конец периода</div>
      <div className="mt-2 text-3xl font-bold">{percent(forecastPct)}</div>
      <div className="mt-1 text-lg font-semibold">
        {props.forecast === null ? '—' : formatCurrency(props.forecast)}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-border pt-3 text-xs">
        <div>
          <div className="text-muted-foreground">До плана</div>
          <div className={remaining === null ? '' : remaining >= 0 ? 'text-success' : 'text-error'}>
            {remaining === null ? '—' : `${remaining >= 0 ? '+' : ''}${formatCurrency(remaining)}`}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Выполнение</div>
          <div className="font-semibold">{percent(forecastPct)}</div>
        </div>
      </div>
    </Card>
  )
}

export function ShmpManualPlanCard(props: {
  totalPlan: number
  rows: ShmpCourseWeeklyPlanRow[]
  onEdit: () => void
}) {
  const totalStudios = props.rows.reduce(
    (sum, row) => sum + (row.planned_studio_count ?? 0),
    0
  )
  const lastUpdatedRow = [...props.rows]
    .filter((row) => row.updated_at || row.created_at)
    .sort((a, b) => {
      const aTime = new Date(a.updated_at ?? a.created_at ?? 0).getTime()
      const bTime = new Date(b.updated_at ?? b.created_at ?? 0).getTime()
      return bTime - aTime
    })[0]

  return (
    <Card className="h-full border border-border/70 shadow-none">
      <div className="mb-5 flex items-start justify-between gap-3">
        <CardTitle>Ручной план ШМП</CardTitle>
        <Button size="sm" variant="outline" onClick={props.onEdit} className="px-3 py-1.5 text-xs">
          <Pencil size={14} className="mr-1.5 inline" />
          Изменить
        </Button>
      </div>

      <div className="text-xs text-muted-foreground">Сумма по выбранному диапазону</div>
      <div className="mt-2 text-3xl font-bold">{formatCurrency(props.totalPlan)}</div>

      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-border pt-3 text-xs">
        <div>
          <div className="text-muted-foreground">Студий в плане</div>
          <div className="font-semibold">{number(totalStudios)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Последнее обновление</div>
          <div className="font-semibold">
            {lastUpdatedRow?.updated_at
              ? new Date(lastUpdatedRow.updated_at).toLocaleDateString('ru-RU')
              : lastUpdatedRow?.created_at
                ? new Date(lastUpdatedRow.created_at).toLocaleDateString('ru-RU')
                : '—'}
          </div>
        </div>
      </div>
    </Card>
  )
}

const funnelIcons = [ClipboardList, CalendarDays, UserRoundCheck, ShoppingCart]

export function ShmpFunnelCard({ summary }: { summary: ShmpDashboardSummary }) {
  const stages = [
    { label: 'Заявки', fact: summary.leads_fact, plan: summary.leads_plan, conversion: null },
    { label: 'Записи (CR1)', fact: summary.appointments_fact, plan: summary.appointments_plan, conversion: summary.cr1 },
    { label: 'Приходы (CR2)', fact: summary.visitors_fact, plan: summary.visitors_plan, conversion: summary.cr2 },
    { label: 'Продажи (CR3)', fact: summary.sales_fact, plan: summary.sales_plan, conversion: summary.cr3 },
  ]

  return (
    <Card className="h-full border border-border/70 shadow-none">
      <CardTitle>Воронка отдела ШМП</CardTitle>
      <div className="grid grid-cols-2 gap-y-5 md:grid-cols-4">
        {stages.map((stage, index) => {
          const Icon = funnelIcons[index]
          return (
            <div key={stage.label} className="relative flex flex-col items-center px-2 text-center">
              <div className="text-xs font-semibold">{stage.label}</div>
              <div className="my-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-[#f3a800]">
                <Icon size={22} />
              </div>
              <div className="text-lg font-bold">{number(stage.fact)}</div>
              <div className="text-[10px] text-muted-foreground">
                План {number(stage.plan)}
              </div>
              {index > 0 && (
                <div className="absolute -left-5 top-[55px] hidden items-center gap-1 text-[#f3a800] md:flex">
                  <span className="rounded-md border border-border bg-card px-2 py-1 text-xs font-semibold text-foreground">
                    {percent(stage.conversion)}
                  </span>
                  <ArrowRight size={18} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

export function ShmpCoursesCard(props: {
  courses: ShmpCourseSummary[]
  summary: ShmpDashboardSummary
}) {
  return (
    <Card className="h-full border border-border/70 shadow-none">
      <CardTitle>Курсы: 1 / 2 / 3</CardTitle>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[390px] text-xs">
          <thead className="text-left text-[10px] text-muted-foreground">
            <tr>
              <th className="pb-2 font-medium">Курс</th>
              <th className="pb-2 font-medium">Франчайзи</th>
              <th className="pb-2 font-medium">Выручка</th>
              <th className="pb-2 text-right font-medium">% плана</th>
            </tr>
          </thead>
          <tbody>
            {props.courses.map(course => (
              <tr key={course.course_number} className="border-t border-border/60">
                <td className="py-2.5 font-semibold">{course.course_number} курс</td>
                <td>{number(course.partner_count)}</td>
                <td className="font-medium">{formatCurrency(course.revenue_fact)}</td>
                <td className={`text-right font-semibold ${tone(course.plan_completion_pct)}`}>
                  {percent(course.plan_completion_pct)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border font-semibold">
            <tr>
              <td className="pt-2.5">Всего</td>
              <td className="pt-2.5">{number(props.summary.partner_count)}</td>
              <td className="pt-2.5">{formatCurrency(props.summary.revenue_fact)}</td>
              <td className={`pt-2.5 text-right ${tone(props.summary.plan_completion_pct)}`}>
                {percent(props.summary.plan_completion_pct)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  )
}

export function ShmpRankingCard(props: {
  title: string
  partners: ShmpPartnerMetrics[]
  negative?: boolean
}) {
  return (
    <Card className="h-full border border-border/70 shadow-none">
      <CardTitle>{props.title}</CardTitle>
      <div className="space-y-2.5">
        {props.partners.map((partner, index) => (
          <div key={partner.partner_number} className="grid grid-cols-[28px_1fr_auto_auto] items-center gap-2 text-xs">
            <span className={`flex h-6 w-6 items-center justify-center rounded-md font-semibold ${
              props.negative ? 'bg-error/10 text-error' : 'bg-success/10 text-success'
            }`}>
              {index + 1}
            </span>
            <span className="min-w-0 truncate font-medium">
              {partner.persons_fio || `Франчайзи №${partner.partner_number}`}
            </span>
            <span className="whitespace-nowrap text-muted-foreground">
              {formatShmpCourse(partner.course_numbers, partner.course_number)}
            </span>
            <span className={`min-w-[110px] text-right font-semibold ${
              props.negative ? 'text-error' : 'text-success'
            }`}>
              {formatCurrency(partner.revenue_fact)} · {partner.revenue_growth_pct !== null && partner.revenue_growth_pct >= 0 ? '+' : ''}
              {percent(partner.revenue_growth_pct)}
            </span>
          </div>
        ))}
        {props.partners.length === 0 && (
          <div className="py-5 text-center text-xs text-muted-foreground">
            Нет данных предыдущего периода для сравнения.
          </div>
        )}
      </div>
    </Card>
  )
}
